import moment from "moment"

// Lots are linked to their auction through `client_id` (the same id used by
// /property-auctions/previous-auction-results/{id}-auction/).
export const getAuctionId = hit => {
  const id = hit?.client_id ?? hit?.clientId
  return id === undefined || id === null || id === "" ? null : String(id)
}

// Lot URL segment is `{auctionId}-auction`, e.g. `123-auction`.
export const getAuctionIdFromSegment = segment => {
  const match = /^(.+)-auction$/.exec(segment || "")
  return match ? match[1] : null
}

export const getAuctionDateValue = hit =>
  hit?.auctionDate || hit?.auction_date || hit?.date || null

// The feed sends dates like "2026-10-15 17:00:00"
export const parseAuctionDate = value =>
  moment(value, [moment.ISO_8601, "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD"])

const getSourceLotNumber = hit => {
  const num = parseFloat(hit?.lotNumber ?? hit?.extra?.lotNumber)
  return Number.isNaN(num) ? null : num
}

// Lot numbers restart at 1 for every auction, ordered by the lot number the feed gave.
export const numberLotsForAuction = (lots = []) =>
  lots
    .map((lot, index) => ({ lot, index, source: getSourceLotNumber(lot) }))
    .sort((a, b) => {
      if (a.source === null && b.source === null) return a.index - b.index
      if (a.source === null) return 1
      if (b.source === null) return -1
      return a.source - b.source || a.index - b.index
    })
    .map((item, position) => ({ ...item.lot, auctionLotNumber: position + 1 }))

const getEarliestAuctionDate = lots => {
  let earliest = null
  lots.forEach(lot => {
    const value = getAuctionDateValue(lot)
    if (!value) return
    const date = parseAuctionDate(value)
    if (!date.isValid()) return
    if (!earliest || date.isBefore(earliest)) earliest = date
  })
  // Local time without an offset, so the time shown matches the feed
  return earliest ? earliest.format("YYYY-MM-DDTHH:mm:ss") : null
}

// Returns one entry per auction, sorted by auction date. Lots without a
// client_id cannot be attached to an auction, so they are skipped.
export const groupLotsByAuction = (hits = []) => {
  const lotsByAuction = new Map()

  hits.forEach(hit => {
    const auctionId = getAuctionId(hit)
    if (!auctionId) {
      console.warn(
        "Lot skipped, no client_id to link it to an auction:",
        hit?.objectID
      )
      return
    }
    if (!lotsByAuction.has(auctionId)) lotsByAuction.set(auctionId, [])
    lotsByAuction.get(auctionId).push(hit)
  })

  const auctions = Array.from(lotsByAuction, ([auctionId, lots]) => {
    const numberedLots = numberLotsForAuction(lots)
    const firstWithImage = numberedLots.find(lot => lot?.images1?.[0])
    return {
      auctionId,
      auctionDate: getEarliestAuctionDate(numberedLots),
      image: firstWithImage ? firstWithImage.images1[0] : null,
      lots: numberedLots,
      lotCount: numberedLots.length,
    }
  })

  return auctions.sort((a, b) => {
    if (!a.auctionDate) return 1
    if (!b.auctionDate) return -1
    return moment(a.auctionDate).valueOf() - moment(b.auctionDate).valueOf()
  })
}

export const findAuction = (auctions = [], auctionId) =>
  auctions.find(auction => auction.auctionId === String(auctionId)) || null

// Auction months. The Current auctions page shows one card per month and each
// card links to /property-auctions/for-sale/month-{YYYY-MM}/ for that month's lots.

const MONTH_FORMAT = "YYYY-MM"
const MONTH_SEGMENT = /^month-(\d{4}-\d{2})$/

// e.g. "2026-10"
export const getAuctionMonthKey = hit => {
  const value = getAuctionDateValue(hit)
  if (!value) return null
  const date = parseAuctionDate(value)
  return date.isValid() ? date.format(MONTH_FORMAT) : null
}

export const getAuctionMonthUrl = monthKey =>
  `/property-auctions/for-sale/month-${monthKey}/`

const findMonthSegment = pathname =>
  (pathname || "").split("/").find(part => MONTH_SEGMENT.test(part))

// The `month-{YYYY-MM}/` segment of a results URL, or "" when there is none
export const getAuctionMonthSegment = pathname => {
  const segment = findMonthSegment(pathname)
  return segment ? `${segment}/` : ""
}

export const getAuctionMonthFromPath = pathname => {
  const segment = findMonthSegment(pathname)
  return segment ? MONTH_SEGMENT.exec(segment)[1] : null
}

// One entry per month from the current month on, in date order. The current
// month is live, later months are upcoming. Only future lots are counted, as
// they are the lots /property-auctions/for-sale/ lists for the month.
export const groupLotsByMonth = (hits = [], now = moment()) => {
  const currentMonth = now.format(MONTH_FORMAT)
  const lotsByMonth = new Map()

  hits.forEach(hit => {
    if (hit?.auctionType && hit.auctionType !== "future") return
    const monthKey = getAuctionMonthKey(hit)
    if (!monthKey || monthKey < currentMonth) return
    if (!lotsByMonth.has(monthKey)) lotsByMonth.set(monthKey, [])
    lotsByMonth.get(monthKey).push(hit)
  })

  const months = Array.from(lotsByMonth, ([monthKey, lots]) => {
    const firstWithImage = lots.find(lot => lot?.images1?.[0])
    const slotsByDay = new Map()
    lots.forEach(lot => {
      const day = parseAuctionDate(getAuctionDateValue(lot)).format("YYYY-MM-DD")
      slotsByDay.set(day, (slotsByDay.get(day) || 0) + 1)
    })
    return {
      monthKey,
      auctionDate: getEarliestAuctionDate(lots),
      image: firstWithImage ? firstWithImage.images1[0] : null,
      lotCount: lots.length,
      // Slots per auction day, in date order, e.g. [{ day: "2026-10-15", count: 3 }]
      days: Array.from(slotsByDay, ([day, count]) => ({ day, count })).sort(
        (a, b) => a.day.localeCompare(b.day)
      ),
      isLive: monthKey === currentMonth,
    }
  })

  return months.sort((a, b) => a.monthKey.localeCompare(b.monthKey))
}

// auctionDate is a string in Algolia and cannot be filtered there, so a month
// is filtered after the search and paged here instead of by Algolia.
export const getMonthResults = (
  hits = [],
  monthKey,
  pageVal = 1,
  hitsPerPage = 16
) => {
  const monthHits = hits.filter(hit => getAuctionMonthKey(hit) === monthKey)
  const page = Math.max(parseInt(pageVal, 10) || 1, 1) - 1

  return {
    total: monthHits.length,
    current_page_number: page,
    hitsPerPage,
    number_of_pages: Math.ceil(monthHits.length / hitsPerPage),
    hits: monthHits.slice(page * hitsPerPage, (page + 1) * hitsPerPage),
  }
}
